/**
 * IndentCreationDialog - Unified Indent Creation Form
 * 
 * This is the SINGLE SOURCE OF TRUTH for indent creation across the application.
 * Used by:
 * - CreateIndentPage.js (manual indent creation)
 * - PurchaseFulfillmentPage.js (purchase fulfillment register)
 * 
 * DO NOT create duplicate indent forms elsewhere.
 * Any changes to indent creation flow should be made HERE.
 * 
 * SO Linkage Feature:
 * - Multi-select dropdown for linking indents to multiple SOs
 * - "Stock" option for general stock purchases (not linked to any SO)
 * - Fetches linked SOs dynamically based on item demand
 * - Stores as so_numbers array: ["CALPN0183L", "Stock"]
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { FileText, Loader2, Truck, Factory, ChevronDown, Package } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Props:
 * @param {boolean} open - Whether dialog is open
 * @param {function} onOpenChange - Callback when dialog open state changes
 * @param {Array} items - Items to create indents for. Each item should have:
 *   - item_id: string
 *   - item_name: string
 *   - item_code: string (optional)
 *   - unit: string (default 'NOS')
 *   - source_type: string (item's MP/IP type for display)
 *   - lead_time: number (days)
 *   - nett_status: number
 *   - default_qty: number (suggested indent quantity)
 *   - location_code: string (optional)
 *   - location_id: string (optional)
 *   - available_sos: Array of {id, number} (optional - pre-provided linked SOs)
 *   - default_so_numbers: Array of strings (optional - pre-selected SO numbers)
 * @param {function} onSuccess - Callback when indents are created successfully
 * @param {string} title - Dialog title (default: "Create Indents")
 * @param {string} description - Dialog description
 * @param {boolean} desiredPoDateRequired - Whether desired PO date is required (default: false)
 * @param {string} sourceContext - Context for indent creation: 'manual' | 'purchase_fulfillment'
 */
export default function IndentCreationDialog({
  open,
  onOpenChange,
  items = [],
  onSuccess,
  title = "Create Indents",
  description = "Create purchase requisitions for selected items.",
  desiredPoDateRequired = false,
  sourceContext = 'manual'
}) {
  const { getAuthHeader } = useAuth();
  
  // Form state
  const [desiredPoDate, setDesiredPoDate] = useState('');
  const [notes, setNotes] = useState('');
  const [itemsData, setItemsData] = useState([]);
  const [creating, setCreating] = useState(false);
  
  // Linked SOs per item (fetched dynamically)
  const [linkedSOsMap, setLinkedSOsMap] = useState({}); // { itemId: [{ id, so_number, client_name }] }
  const [loadingSOsMap, setLoadingSOsMap] = useState({}); // { itemId: boolean }

  // Fetch linked SOs for a specific item
  const fetchLinkedSOs = useCallback(async (itemId, currentMap) => {
    if (!itemId || currentMap[itemId]) return; // Already fetched
    
    setLoadingSOsMap(prev => ({ ...prev, [itemId]: true }));
    try {
      const response = await axios.get(`${API_URL}/items/${itemId}/linked-sos`, {
        headers: getAuthHeader()
      });
      const linkedSOs = response.data?.linked_sos || [];
      setLinkedSOsMap(prev => ({ ...prev, [itemId]: linkedSOs }));
    } catch (error) {
      console.error(`Failed to fetch linked SOs for item ${itemId}:`, error);
      setLinkedSOsMap(prev => ({ ...prev, [itemId]: [] }));
    } finally {
      setLoadingSOsMap(prev => ({ ...prev, [itemId]: false }));
    }
  }, [getAuthHeader]);

  // Initialize items data when dialog opens or items change
  useEffect(() => {
    if (open && items.length > 0) {
      const initialData = items.map(item => ({
        item_id: item.item_id || item.id,
        item_name: item.item_name || item.name,
        item_code: item.item_code || '',
        unit: item.unit || 'NOS',
        source_type: item.source_type || 'MP',
        lead_time: item.lead_time || 0,
        nett_status: item.nett_status ?? 0,
        location_code: item.location_code || item.default_location_code || '',
        location_id: item.location_id || item.default_location_id || '',
        indent_qty: item.default_qty || item.indent_qty || Math.max(1, Math.abs(item.nett_status || 0)),
        available_sos: item.available_sos || [],
        // Multi-select: array of selected SO numbers (including "Stock" option)
        selected_so_numbers: item.default_so_numbers || []
      }));
      setItemsData(initialData);
      setDesiredPoDate('');
      setNotes('');
      
      // Reset and fetch linked SOs for each item (only if not already provided)
      const newLinkedSOsMap = {};
      initialData.forEach(item => {
        if (item.available_sos && item.available_sos.length > 0) {
          // PF page already provided linked SOs — use directly
          newLinkedSOsMap[item.item_id] = item.available_sos.map(s => ({
            id: s.id,
            so_number: s.so_number || s.number || '',
            client_name: s.client_name || '',
            delivery_date: s.delivery_date || '',
            pending_qty: s.pending_qty || 0,
            link_type: 'pf_source'
          }));
        }
      });
      setLinkedSOsMap(newLinkedSOsMap);
      setLoadingSOsMap({});
      
      // Fetch from API only for items without pre-provided SOs
      initialData.forEach(item => {
        if (!newLinkedSOsMap[item.item_id]) {
          fetchLinkedSOs(item.item_id, newLinkedSOsMap);
        }
      });
    }
  }, [open, items, fetchLinkedSOs]);

  const updateItemQty = (index, qty) => {
    setItemsData(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], indent_qty: parseFloat(qty) || 0 };
      return updated;
    });
  };

  // Toggle SO selection (multi-select)
  const toggleSOSelection = (index, soNumber) => {
    setItemsData(prev => {
      const updated = [...prev];
      const currentSelections = updated[index].selected_so_numbers || [];
      
      if (currentSelections.includes(soNumber)) {
        // Remove from selection
        updated[index] = {
          ...updated[index],
          selected_so_numbers: currentSelections.filter(s => s !== soNumber)
        };
      } else {
        // Add to selection
        updated[index] = {
          ...updated[index],
          selected_so_numbers: [...currentSelections, soNumber]
        };
      }
      return updated;
    });
  };

  const handleCreate = async () => {
    // Validation
    if (desiredPoDateRequired && !desiredPoDate) {
      toast.error('Please select a Desired PO Date');
      return;
    }

    const validItems = itemsData.filter(item => item.indent_qty > 0);
    if (validItems.length === 0) {
      toast.error('Please enter quantity for at least one item');
      return;
    }

    setCreating(true);
    try {
      let createdCount = 0;
      let errors = [];

      // Create indents one by one using the unified /api/indents endpoint
      for (const item of validItems) {
        try {
          // Build so_numbers array
          const soNumbers = item.selected_so_numbers || [];
          
          await axios.post(`${API_URL}/indents`, {
            item_id: item.item_id,
            quantity_required: item.indent_qty,
            // New format: so_numbers array
            so_numbers: soNumbers.length > 0 ? soNumbers : [],
            // Legacy format for backward compatibility (first SO or null)
            sales_order_id: null,
            so_number: soNumbers.length > 0 ? soNumbers[0] : null,
            desired_po_date: desiredPoDate || null,
            reason: notes || (sourceContext === 'manual' ? 'Manual indent from Create Indent page' : `From Purchase Fulfillment`)
          }, { headers: getAuthHeader() });
          createdCount++;
        } catch (error) {
          const msg = error.response?.data?.detail || `Failed for ${item.item_name}`;
          errors.push(typeof msg === 'string' ? msg : JSON.stringify(msg));
        }
      }

      if (createdCount > 0) {
        toast.success(`Created ${createdCount} indent(s)`);
        onOpenChange(false);
        if (onSuccess) onSuccess();
      }
      
      if (errors.length > 0) {
        toast.error(`${errors.length} error(s): ${errors.slice(0, 2).join(', ')}`);
      }
    } catch (error) {
      console.error('Error creating indents:', error);
      toast.error('Failed to create indents');
    } finally {
      setCreating(false);
    }
  };

  // Get available SOs for an item (from pre-provided or dynamically fetched)
  const getAvailableSOsForItem = (item) => {
    // First check pre-provided available_sos
    if (item.available_sos && item.available_sos.length > 0) {
      return item.available_sos.map(so => ({
        so_number: so.number || so.so_number,
        client_name: so.client_name || '',
        delivery_date: so.delivery_date || '',
        pending_qty: so.pending_qty || 0
      }));
    }
    // Then check dynamically fetched linked SOs
    const linked = linkedSOsMap[item.item_id] || [];
    return linked;
  };

  const formatNettStatus = (value) => {
    if (value === undefined || value === null) return '-';
    return value.toFixed(2);
  };

  // Format selected SOs for display
  const formatSelectedSOs = (selectedSoNumbers) => {
    if (!selectedSoNumbers || selectedSoNumbers.length === 0) {
      return 'Select SOs...';
    }
    if (selectedSoNumbers.length === 1) {
      return selectedSoNumbers[0];
    }
    return `${selectedSoNumbers.length} selected`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="indent-creation-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-orange-600" />
            {title}
          </DialogTitle>
          <DialogDescription>
            {description} {itemsData.length > 0 && `(${itemsData.length} item(s))`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Desired PO Date */}
          <div className="flex items-center gap-4">
            <Label htmlFor="desired-po-date" className="whitespace-nowrap">
              Desired PO Date {desiredPoDateRequired && <span className="text-red-500">*</span>}
            </Label>
            <Input
              id="desired-po-date"
              type="date"
              value={desiredPoDate}
              onChange={(e) => setDesiredPoDate(e.target.value)}
              className="w-[180px]"
              min={new Date().toISOString().split('T')[0]}
              data-testid="indent-desired-po-date"
            />
          </div>

          {/* Items Table */}
          <div className="border rounded-lg max-h-[400px] overflow-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="min-w-[200px]">Item</TableHead>
                  <TableHead className="text-center w-[80px]">Location</TableHead>
                  <TableHead className="text-center w-[60px]">Source</TableHead>
                  <TableHead className="text-center w-[60px]">Lead</TableHead>
                  <TableHead className="text-right w-[80px]">Nett</TableHead>
                  <TableHead className="text-right w-[100px]">Qty</TableHead>
                  <TableHead className="w-[200px]">Link to SO(s)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {itemsData.map((item, index) => {
                  const availableSOs = getAvailableSOsForItem(item);
                  const isLoadingSOs = loadingSOsMap[item.item_id];
                  const selectedSoNumbers = item.selected_so_numbers || [];
                  
                  return (
                    <TableRow key={item.item_id || index}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{item.item_name}</p>
                          <p className="text-xs text-muted-foreground">{item.unit}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        {item.location_code ? (
                          <Badge variant="outline" className="text-xs">{item.location_code}</Badge>
                        ) : (
                          <span className="text-muted-foreground text-xs">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex items-center justify-center gap-1">
                          {item.source_type === 'IP' ? (
                            <Factory className="w-3 h-3 text-purple-600" />
                          ) : (
                            <Truck className="w-3 h-3 text-blue-600" />
                          )}
                          <span className="text-xs">{item.source_type || 'MP'}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-center">
                        {item.lead_time > 0 ? `${item.lead_time}d` : '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <span className={item.nett_status < 0 ? 'text-red-600 font-medium' : ''}>
                          {formatNettStatus(item.nett_status)}
                        </span>
                      </TableCell>
                      <TableCell className="text-right">
                        <Input
                          type="number"
                          value={item.indent_qty}
                          onChange={(e) => updateItemQty(index, e.target.value)}
                          className="w-[100px] text-right"
                          min="0"
                          data-testid={`indent-qty-${index}`}
                        />
                      </TableCell>
                      <TableCell>
                        {/* Multi-select SO dropdown */}
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="w-full justify-between text-xs h-9"
                              disabled={isLoadingSOs}
                              data-testid={`indent-so-select-${index}`}
                            >
                              {isLoadingSOs ? (
                                <span className="flex items-center gap-1">
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                  Loading...
                                </span>
                              ) : (
                                <span className="truncate">{formatSelectedSOs(selectedSoNumbers)}</span>
                              )}
                              <ChevronDown className="w-3 h-3 ml-1 shrink-0" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-[280px] p-2" align="start">
                            <div className="space-y-1 max-h-[250px] overflow-auto">
                              {/* Stock option */}
                              <label
                                className="flex items-center gap-2 p-2 hover:bg-muted rounded cursor-pointer"
                              >
                                <Checkbox
                                  checked={selectedSoNumbers.includes('Stock')}
                                  onCheckedChange={() => toggleSOSelection(index, 'Stock')}
                                />
                                <div className="flex items-center gap-2">
                                  <Package className="w-4 h-4 text-gray-500" />
                                  <span className="font-medium">Stock</span>
                                </div>
                                <span className="text-xs text-muted-foreground ml-auto">(General)</span>
                              </label>
                              
                              {/* Divider if there are linked SOs */}
                              {availableSOs.length > 0 && (
                                <div className="border-t my-1" />
                              )}
                              
                              {/* Linked SOs */}
                              {availableSOs.length > 0 ? (
                                availableSOs.map(so => (
                                  <label
                                    key={so.so_number}
                                    className="flex items-center gap-2 p-2 hover:bg-muted rounded cursor-pointer"
                                  >
                                    <Checkbox
                                      checked={selectedSoNumbers.includes(so.so_number)}
                                      onCheckedChange={() => toggleSOSelection(index, so.so_number)}
                                    />
                                    <div className="flex-1 min-w-0">
                                      <p className="font-medium text-sm">{so.so_number}</p>
                                      {so.client_name && (
                                        <p className="text-xs text-muted-foreground truncate">{so.client_name}</p>
                                      )}
                                      {so.delivery_date && (
                                        <p className="text-xs text-blue-500">Del: {so.delivery_date?.split('T')[0]}</p>
                                      )}
                                    </div>
                                  </label>
                                ))
                              ) : !isLoadingSOs && (
                                <p className="text-xs text-muted-foreground p-2">
                                  No linked SOs found for this item
                                </p>
                              )}
                            </div>
                            
                            {/* Selected count */}
                            {selectedSoNumbers.length > 0 && (
                              <div className="border-t mt-2 pt-2">
                                <p className="text-xs text-muted-foreground">
                                  Selected: {selectedSoNumbers.join(', ')}
                                </p>
                              </div>
                            )}
                          </PopoverContent>
                        </Popover>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="indent-notes">Notes (Optional)</Label>
            <Textarea
              id="indent-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any additional notes for these indents..."
              rows={2}
              data-testid="indent-notes"
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={creating}>
            Cancel
          </Button>
          <Button onClick={handleCreate} disabled={creating || itemsData.length === 0}>
            {creating ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <FileText className="w-4 h-4 mr-2" />
                Create {itemsData.filter(i => i.indent_qty > 0).length} Indent(s)
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
